import type { SseEvent, SseEventType } from "@redchain/contracts";

type Handler<T extends SseEventType> = (ev: Extract<SseEvent, { type: T }>) => void;
type AnyHandler = (ev: SseEvent) => void;

/** Process-wide fan-out of SSE events, fed by SessionClient's stream. */
class SseBus {
  private readonly typed = new Map<SseEventType, Set<AnyHandler>>();
  private readonly all = new Set<AnyHandler>();

  on<T extends SseEventType>(type: T, handler: Handler<T>): () => void {
    let set = this.typed.get(type);
    if (!set) this.typed.set(type, (set = new Set()));
    set.add(handler as AnyHandler);
    return () => set.delete(handler as AnyHandler);
  }

  onAny(handler: AnyHandler): () => void {
    this.all.add(handler);
    return () => this.all.delete(handler);
  }

  emit(ev: SseEvent): void {
    for (const h of this.typed.get(ev.type) ?? []) h(ev);
    for (const h of this.all) h(ev);
  }

  /** Resolves on the next event of `type`, or after `timeoutMs`. */
  next<T extends SseEventType>(type: T, timeoutMs: number): Promise<Extract<SseEvent, { type: T }> | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        off();
        resolve(null);
      }, timeoutMs);
      const off = this.on(type, (ev) => {
        clearTimeout(timer);
        off();
        resolve(ev);
      });
    });
  }
}

export const sseBus = new SseBus();
